import { useCallback, useEffect, useState } from 'react';
import { toggleDrawer } from '@helpers';
import { useChatState } from '@core/chat';
import { ToggleDrawerType } from '@core/dashboard/interfaces';
import { useConference } from '@core/dashboard/components/DashboardContent/Dolby';
import { useOverlayContext } from '@providers/OverlayProvider';
import { useClientSpecificChatDrawerWidth } from '@hooks/clientSpecificUtils';
import { useSearchUserContext } from '@providers/SearchUserProvider';
import OVERLAYS from '@constants/overlays';

import ChatDrawerView from './ChatDrawerView';

const ChatDrawer = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { setFocusOnSearchField } = useSearchUserContext();
  const {
    addToCurrentlyOpenOverlays,
    removeFromCurrentlyOpenOverlays,
    isOverlayCurrentlyOpen,
    hideCurrentlyOpenOverlays,
  } = useOverlayContext();

  const { screenMediaStreams } = useConference();
  const {
    open: isOpenChat,
    setOpen,
    initialLoading,
    streamClient,
    setChatDrawerWidth,
  } = useChatState();

  const { chatDrawerWidth } = useClientSpecificChatDrawerWidth();

  useEffect(() => {
    setChatDrawerWidth(chatDrawerWidth);
  }, [chatDrawerWidth]);

  const handleToggleDrawer: ToggleDrawerType = useCallback(
    (event) => {
      toggleDrawer(event, () => {
        if (!isOpenChat) {
          addToCurrentlyOpenOverlays(OVERLAYS.CHAT);
        } else {
          removeFromCurrentlyOpenOverlays(OVERLAYS.CHAT);
        }
        setOpen(!isOpenChat);
        setFocusOnSearchField(!isOpenChat);
      });
    },
    [isOpenChat, setOpen, setFocusOnSearchField],
  );

  const autoClose = useCallback(() => {
    if (isOpenChat) {
      setOpen(false);
      setFocusOnSearchField(false);
      removeFromCurrentlyOpenOverlays(OVERLAYS.CHAT);
    }
  }, [setOpen, setFocusOnSearchField, isOpenChat]);

  useEffect(() => {
    if (hideCurrentlyOpenOverlays) {
      setOpen(false);
    } else {
      if (isOverlayCurrentlyOpen(OVERLAYS.CHAT)) {
        setOpen(true);
      }
    }
  }, [hideCurrentlyOpenOverlays, isOverlayCurrentlyOpen]);

  if (!streamClient) {
    return null;
  }

  return (
    <ChatDrawerView
      isOpenDrawer={isOpenChat}
      toggleDrawer={handleToggleDrawer}
      isLoading={initialLoading}
      hasScreenMediaStreams={!!screenMediaStreams?.length}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
      autoClose={autoClose}
    />
  );
};

export default ChatDrawer;
